/**
 * Product Cache Utility
 * Implements caching mechanism to prevent unnecessary API calls
 */

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}

class ProductCacheManager {
    private cache: Map<string, CacheEntry<any>> = new Map();
    private defaultTTL: number = 5 * 60 * 1000; // 5 minutes default TTL

    /**
     * Generate cache key from filter parameters
     */
    private generateKey(filters: Record<string, any>): string {
        const sortedKeys = Object.keys(filters).sort();
        const keyParts = sortedKeys.map(key => `${key}:${filters[key]}`);
        return keyParts.join('|');
    }

    /**
     * Get cached data if available and not expired
     */
    get<T>(filters: Record<string, any>): T | null {
        const key = this.generateKey(filters);
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        // Check if cache has expired
        if (Date.now() > entry.expiresAt) {
            this.cache.delete(key);
            return null;
        }

        return entry.data as T;
    }

    /**
     * Set data in cache with optional TTL
     */
    set<T>(filters: Record<string, any>, data: T, ttl?: number): void {
        const key = this.generateKey(filters);
        const timestamp = Date.now();
        const expiresAt = timestamp + (ttl || this.defaultTTL);

        this.cache.set(key, {
            data,
            timestamp,
            expiresAt,
        });
    }

    /**
     * Check if cache has valid data for given filters
     */
    has(filters: Record<string, any>): boolean {
        return this.get(filters) !== null;
    }

    /**
     * Clear specific cache entry
     */
    clear(filters: Record<string, any>): void {
        const key = this.generateKey(filters);
        this.cache.delete(key);
    }

    /**
     * Clear all cache entries
     */
    clearAll(): void {
        this.cache.clear();
    }

    /**
     * Clear expired entries
     */
    clearExpired(): void {
        const now = Date.now();
        for (const [key, entry] of this.cache.entries()) {
            if (now > entry.expiresAt) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Get cache statistics
     */
    getStats(): { size: number; keys: string[] } {
        return {
            size: this.cache.size,
            keys: Array.from(this.cache.keys()),
        };
    }
}

// Export singleton instance
export const productCache = new ProductCacheManager();
